import React from 'react';
import { Box, Typography } from '@mui/material';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { makeStyles } from 'tss-react/mui';
import { Theme } from '@mui/material/styles';
import { FormProvider, useForm } from 'react-hook-form';
import FormCheckboxField from './formFields/FormCheckboxField';

const useStyles = makeStyles()((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  sectionHeader: {
    fontSize: '20px',
    fontWeight: 600,
    color: theme.palette.common.white,
    fontFamily: 'Poppins',
    marginBottom: '16px',
  },
  messageBox: {
    backgroundColor: theme.palette.primary.main,
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    padding: '20px 24px',
    fontSize: '13px',
    fontWeight: 300,
    color: 'rgba(255, 255, 255, 0.7)',
    fontFamily: 'Inter',
    lineHeight: '1.9',
    marginBottom: '16px',
  },
  termsLinkContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '20px',
  },
  termsIcon: {
    fontSize: '18px',
    color: theme.palette.info.main,
  },
  termsLink: {
    fontSize: '14px',
    fontWeight: 500,
    color: theme.palette.common.white,
    fontFamily: 'Inter',
    cursor: 'pointer',
    textDecoration: 'underline',
    '&:hover': {
      color: 'rgba(255, 255, 255, 0.8)',
    },
  },
  checkboxContainer: {
    '& .MuiFormControlLabel-label': {
      fontSize: '14px',
      fontWeight: 500,
      color: theme.palette.common.white,
    },
  },
}));

interface ConsentAuthorizationSectionProps {
  authorizationMessage: string;
  isAgreed: boolean;
  onAgreementChange: (checked: boolean) => void;
  onTermsClick?: () => void;
}

const ConsentAuthorizationSection = ({
  authorizationMessage,
  isAgreed,
  onAgreementChange,
  onTermsClick,
}: ConsentAuthorizationSectionProps) => {
  const { classes } = useStyles();

  const formMethods = useForm({
    defaultValues: {
      consentAgreement: isAgreed,
    },
  });

  const handleTermsClick = () => {
    // If custom click handler is provided, use it
    if (onTermsClick) {
      onTermsClick();
    }
  };

  const handleCheckboxChange = (value: boolean) => {
    onAgreementChange(value);
    formMethods.setValue('consentAgreement', value);
  };

  // Sync external isAgreed prop with form state
  React.useEffect(() => {
    formMethods.setValue('consentAgreement', isAgreed);
  }, [isAgreed, formMethods]);

  return (
    <Box className={classes.container}>
      <Typography className={classes.sectionHeader}>
        Consent & Authorization
      </Typography>

      <Box className={classes.messageBox}>
        <Typography
          component="div"
          sx={{ whiteSpace: 'pre-line' }}
          dangerouslySetInnerHTML={{ __html: authorizationMessage }}
        />
      </Box>

      <Box className={classes.termsLinkContainer}>
        <OpenInNewOutlinedIcon className={classes.termsIcon} />
        <Typography
          className={classes.termsLink}
          onClick={handleTermsClick}
          component="span"
        >
          View detailed Terms & Conditions
        </Typography>
      </Box>

      <FormProvider {...formMethods}>
        <Box className={classes.checkboxContainer}>
          <Box
            onClick={() => handleCheckboxChange(!formMethods.getValues('consentAgreement'))}
            sx={{ cursor: 'pointer', display: 'inline-flex' }}
          >
            <FormCheckboxField
              name="consentAgreement"
              label="I agree to the Terms & Conditions."
            />
          </Box>
        </Box>
      </FormProvider>
    </Box>
  );
};

export default ConsentAuthorizationSection;